import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'qprice.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Цвет строки состояния мобильного браузера — совпадает с `--qp2-bg`.
    В hex, потому что `<meta theme-color>` не понимает oklch. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#f3f7f4',
  dark: '#111214',
};

interface ThemeContextValue {
  /** Что выбрал пользователь, включая «как в системе». */
  preference: ThemePreference;
  /** Тема, которая реально нарисована на экране. */
  theme: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
  /** Переключает light ⇄ dark, снимая режим «как в системе». */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // приватный режим или заблокированное хранилище — просто идём за системой
  }
  return 'system';
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

function resolve(preference: ThemePreference, system: ResolvedTheme): ResolvedTheme {
  return preference === 'system' ? system : preference;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // Системную тему слушаем всегда, а не только в режиме 'system': иначе после
  // возврата к «как в системе» экран останется в теме, снятой при монтировании.
  useEffect(() => {
    const mql = window.matchMedia?.(DARK_QUERY);
    if (!mql) return;
    const onChange = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme = resolve(preference, system);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEME_COLOR[theme]);
  }, [theme]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // не смогли запомнить — тема всё равно применится на эту сессию
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(resolve(preference, system) === 'dark' ? 'light' : 'dark');
  }, [preference, system, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, setPreference, toggle }),
    [preference, theme, setPreference, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme должен вызываться внутри <ThemeProvider>');
  return ctx;
}
